import React, { useEffect, useRef, useState } from 'react';

export const MIN_COLUMN_WIDTH = 80;
export const MAX_COLUMN_WIDTH = 600;
export const COLUMN_RESIZE_STEP = 10;
export const COLUMN_RESIZE_HANDLE_CLASS =
  'absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize select-none hover:bg-[#7F56D9]/40 focus:outline-none focus-visible:bg-[#7F56D9]/40';

export const clampColumnWidth = (width: number): number =>
  Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)));

// Keeps only finite numeric widths, clamped, so a corrupt or hand-edited entry can't break the layout.
export function normalizeColumnWidths(saved: unknown): Record<string, number> {
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
  return Object.entries(saved as Record<string, unknown>).reduce<Record<string, number>>((acc, [key, value]) => {
    if (typeof value === 'number' && Number.isFinite(value)) acc[key] = clampColumnWidth(value);
    return acc;
  }, {});
}

const readWidths = (storageKey: string | null): Record<string, number> => {
  if (!storageKey) return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    return normalizeColumnWidths(raw ? JSON.parse(raw) : null);
  } catch {
    return {};
  }
};

const writeWidths = (storageKey: string | null, widths: Record<string, number>): void => {
  if (!storageKey) return;
  try {
    if (Object.keys(widths).length === 0) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    }
  } catch {
    // localStorage can be unavailable (private mode, quota); widths still apply for this session.
  }
};

/**
 * Per-column widths adjusted by dragging a header's resize handle, persisted under `storageKey`
 * (null disables persistence). Unset columns use `defaultWidths[key]`, else `fallbackWidth`.
 */
export function useResizableColumns(
  storageKey: string | null,
  defaultWidths: Readonly<Record<string, number>>,
  fallbackWidth: number
) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => readWidths(storageKey));
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const widthsRef = useRef(columnWidths);
  widthsRef.current = columnWidths;
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setColumnWidths(readWidths(storageKey));
  }, [storageKey]);

  useEffect(() => () => cleanupRef.current?.(), []);

  const widthOf = (key: string): number => columnWidths[key] ?? defaultWidths[key] ?? fallbackWidth;

  const commit = (next: Record<string, number>) => {
    setColumnWidths(next);
    writeWidths(storageKey, next);
  };

  const setColumnWidth = (key: string, width: number) =>
    commit({ ...widthsRef.current, [key]: clampColumnWidth(width) });

  const resetColumnWidth = (key: string) => {
    const { [key]: _removed, ...rest } = widthsRef.current;
    commit(rest);
  };

  const resetColumnWidths = () => commit({});

  const startResize = (key: string, startX: number) => {
    const startWidth = widthOf(key);
    setResizingKey(key);

    const onMove = (e: MouseEvent) => {
      setColumnWidths({ ...widthsRef.current, [key]: clampColumnWidth(startWidth + e.clientX - startX) });
    };
    const onUp = () => {
      cleanup();
      writeWidths(storageKey, widthsRef.current);
    };
    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      cleanupRef.current = null;
      setResizingKey(null);
    };

    cleanupRef.current?.();
    cleanupRef.current = cleanup;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const getResizeHandleProps = (key: string, label: string) => ({
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-label': `Resize ${label} column`,
    'aria-valuenow': widthOf(key),
    'aria-valuemin': MIN_COLUMN_WIDTH,
    'aria-valuemax': MAX_COLUMN_WIDTH,
    tabIndex: 0,
    title: 'Drag to resize, double-click to reset',
    draggable: false,
    onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      startResize(key, e.clientX);
    },
    onClick: (e: React.MouseEvent<HTMLElement>) => e.stopPropagation(),
    onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      resetColumnWidth(key);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();
      setColumnWidth(key, widthOf(key) + (e.key === 'ArrowRight' ? COLUMN_RESIZE_STEP : -COLUMN_RESIZE_STEP));
    },
  });

  return {
    columnWidths,
    widthOf,
    isResizing: resizingKey !== null,
    getResizeHandleProps,
    resetColumnWidths,
  };
}
